import { useEffect, useState } from "react";
import { ArrowUpDown, Search } from "lucide-react";
import { Pagination } from "./Pagination";

/**
 * Row render-prop component passed to the row child.
 * When isEditing=true, renders a full-width edit row using FormComponent.
 * FormComponent is required when isEditing is provided.
 */
/**
 * Renders data row always. When isEditing=true, also renders FormComponent <tr> directly below.
 * FormComponent must be a full <tr> with <td> cells.
 */
const RowComponent = ({
  children,
  selected,
  onClick,
  className = "",
  isEditing,
  FormComponent,
}) => (
  <>
    <tr
      onClick={onClick}
      className={`border-b transition-colors
        ${isEditing
          ? "border-green-200 dark:border-green-500/30 bg-green-50/40 dark:bg-green-500/5"
          : `border-gray-100 dark:border-gray-700 ${selected ? "bg-green-50 dark:bg-green-500/10" : "hover:bg-gray-50 dark:hover:bg-slate-700/40"}`
        }
        ${className}`}
    >
      {children}
    </tr>
    {isEditing && (
      <tr className="border-b border-green-200 dark:border-green-500/30">
        <td
          colSpan={100}
          className="px-4 pt-3 pb-4 bg-green-50/80 dark:bg-green-500/5"
        >
          <div className="flex items-start gap-3">
            <div className="w-0.5 self-stretch rounded-full bg-green-400 dark:bg-green-500 flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <p className="text-xs font-semibold text-green-600 dark:text-white-400 uppercase tracking-wide">
                Edit Row
              </p>
              {FormComponent}
            </div>
          </div>
        </td>
      </tr>
    )}
  </>
);

/**
 * Cell render-prop component passed to the row child.
 */
const CellComponent = ({ children, className = "", ...props }) => (
  <td
    className={`px-4 py-3 text-sm text-gray-700 dark:text-gray-300 ${className}`}
    {...props}
  >
    {children}
  </td>
);

/**
 * Table2
 *
 * Props:
 *   headers              string[]          Column header labels
 *   data                 object[]          Row data
 *   isMultiSelect        boolean           Enable row checkboxes
 *   selectedCount        number            Controlled selected count (used only for display; internal state drives actual selection)
 *   bulkActionShowThreshold number         Min selected rows to show bulk action bar
 *   loading              boolean
 *   emptyText            string
 *   children             [rowRenderer, bulkActionsNode?]
 *     rowRenderer        ({ row, index, isSelected, toggleSelect, RowComponent, CellComponent }) => ReactNode
 *     bulkActionsNode    ReactNode         Shown when isMultiSelect && selected >= threshold
 *
 *   onSelectionChange    (selectedIds: any[]) => void   fired when selection changes
 */
const usePagination = ({ paginationData, onPageChange, onRowsPerPageChange }) => {
  const [currentPage, setCurrentPage] = useState(paginationData?.current_page || 1);
  const [pageJump, setPageJump] = useState(String(paginationData?.current_page || 1));
  const [rowsPerPage, setRowsPerPage] = useState(paginationData?.limit || 10);

  const totalPages = paginationData?.total_pages || 1;

  useEffect(() => {
    setCurrentPage(paginationData?.current_page || 1);
    setPageJump(String(paginationData?.current_page || 1));
  }, [paginationData]);

  const goToPage = (page) => {
    const updated = typeof page === "function" ? page(currentPage) : page;
    setCurrentPage(updated);
    onPageChange?.(updated);
  };

  const jumpToPage = () => {
    const page = Number(pageJump);
    if (!Number.isNaN(page)) {
      const valid = Math.min(Math.max(page, 1), totalPages);
      setCurrentPage(valid);
      onPageChange?.(valid);
    }
  };

  const changeRowsPerPage = (n) => {
    setRowsPerPage(n);
    setCurrentPage(1);
    onRowsPerPageChange?.(n);
    onPageChange?.(1);
  };

  return { currentPage, setCurrentPage: goToPage, rowsPerPage, setRowsPerPage: changeRowsPerPage, totalPages, pageJump, setPageJump, jumpToPage };
};

const useTable = ({ data, multiSelect, search, onSortChange, resetSelectionKey }) => {
  const {
    enabled: isMultiSelect = false,
    selectedCount,
    bulkActionShowThreshold = 1,
    keyToSelect = "id",
    onSelectionChange,
  } = multiSelect ?? {};

  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    if (resetSelectionKey === undefined) return;
    setSelectedIds([]);
    onSelectionChange?.([]);
  }, [resetSelectionKey]);

  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const isControlledSearch = search && search.onChange != null;
  const [internalQuery, setInternalQuery] = useState("");
  const searchQuery = isControlledSearch ? (search.value ?? "") : internalQuery;
  const setSearchQuery = isControlledSearch ? search.onChange : setInternalQuery;

  const filteredData =
    search && !isControlledSearch && searchQuery.trim()
      ? data.filter((r) =>
        String(r[search.by] ?? "")
          .toLowerCase()
          .includes(searchQuery.toLowerCase()),
      )
      : data;

  const allIds = filteredData?.map((r) => r[keyToSelect]);
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.includes(id));

  const toggleRow = (id) => {
    const next = selectedIds.includes(id)
      ? selectedIds.filter((x) => x !== id)
      : [...selectedIds, id];
    setSelectedIds(next);
    onSelectionChange?.(next);
  };

  const toggleAll = () => {
    const next = allSelected ? [] : [...allIds];
    setSelectedIds(next);
    onSelectionChange?.(next);
  };

  const handleSort = (label) => {
    const newDir = sortKey === label ? (sortDir === "asc" ? "desc" : "asc") : "asc";
    const newKey = label;
    setSortKey(newKey);
    setSortDir(newDir);
    onSortChange?.({ key: newKey, direction: newDir });
  };

  const effectiveSelected = selectedCount ?? selectedIds.length;
  const bulkActionsEnabled = isMultiSelect && effectiveSelected >= bulkActionShowThreshold;

  return {
    isMultiSelect,
    keyToSelect,
    selectedIds,
    filteredData,
    allSelected,
    toggleRow,
    toggleAll,
    sortKey,
    sortDir,
    handleSort,
    searchQuery,
    setSearchQuery,
    effectiveSelected,
    bulkActionsEnabled,
  };
};

const Table2 = ({
  headers = [],
  data = [],
  multiSelect,
  loading = false,
  emptyText = "No results found.",
  search,
  pagination,
  onSortChange,
  resetSelectionKey,
  children,
}) => {
  const {
    isMultiSelect,
    keyToSelect,
    selectedIds,
    filteredData,
    allSelected,
    toggleRow,
    toggleAll,
    sortKey,
    handleSort,
    searchQuery,
    setSearchQuery,
    effectiveSelected,
    bulkActionsEnabled,
  } = useTable({ data, multiSelect, search, onSortChange, resetSelectionKey });

  const paginationState = usePagination({
    paginationData: pagination?.data,
    onPageChange: pagination?.onPageChange,
    onRowsPerPageChange: pagination?.onRowsPerPageChange,
  });

  const childArray = Array.isArray(children) ? children : [children];
  const rowRenderer = typeof childArray[0] === "function" ? childArray[0] : null;
  const bulkActionsNode = childArray[1] ?? null;

  const showBulkActions = isMultiSelect && bulkActionsNode;
  const skeletonCols = headers.length + (isMultiSelect ? 1 : 0);

  return (
    <div className="bg-white dark:bg-slate-800 shadow-sm rounded-md overflow-hidden">
      {/* Toolbar: search + bulk actions */}
      {(search || showBulkActions) && (
        <div className="flex items-center gap-3 px-4 py-2 border-b border-gray-100 dark:border-slate-700">
          {showBulkActions && (
            <div
              className={`flex items-center gap-3 text-sm transition-opacity ${bulkActionsEnabled ? "opacity-100" : "opacity-40"}`}
            >
              <span className="font-medium text-green-700 dark:text-white-300">
                {effectiveSelected} selected
              </span>
            </div>
          )}
          {search && (
            <div className="relative ml-auto">
              <Search
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={search.placeholder ?? "Search…"}
                className="w-full pl-10 pr-3 py-1 border border-gray-200 bg-gray-50 text-gray-800 focus:outline-none rounded-[6px] focus:ring-2 focus:ring-green-400 transition dark:border-gray-700 dark:bg-slate-800 dark:text-gray-200"
              />
            </div>
          )}
          <div className={`flex items-center gap-2 `}>{bulkActionsNode}</div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="table-auto w-full border-separate border-spacing-y-1">

          <thead>
            <tr className="text-[10px] font-medium text-gray-500 whitespace-nowrap dark:text-gray-400 uppercase tracking-tight">
              {isMultiSelect && (
                <th className="px-4 py-3 text-xs text-center w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    className="h-4 w-4 rounded border-gray-300 text-green-500 focus:ring-green-500"
                  />
                </th>
              )}

              {headers.map((h) => {
                const label = typeof h === "string" ? h : h.label;
                const sortable = typeof h === "string" ? true : h.sortable !== false;
                const k = h?.key ?? label;

                return (
                  <th
                    key={k}
                    onClick={() => sortable && handleSort(k)}
                    className={`px-4 py-3 text-xs transition-colors select-none ${sortable
                        ? "cursor-pointer hover:text-green-500"
                        : "cursor-default"
                      }`}
                  >
                    <div className="flex items-center gap-1">
                      {label}
                      {sortable && (
                        <ArrowUpDown
                          size={12}
                          className={
                            sortKey === k
                              ? "text-green-500"
                              : "text-gray-400 dark:text-gray-500"
                          }
                        />
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: skeletonCols }).map((__, j) => (
                    <td key={j} className="px-3 py-2">
                      <div className="h-4 rounded-full bg-gray-200 dark:bg-slate-700 animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))
            ) : filteredData?.length === 0 ? (
              <tr>
                <td colSpan={skeletonCols}>
                  <div className="flex flex-col items-center justify-center py-14 gap-3 text-gray-400 dark:text-gray-500">
                    <svg width="56" height="56" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="opacity-40">
                      <rect x="8" y="16" width="48" height="36" rx="4" stroke="currentColor" strokeWidth="2.5" fill="none" />
                      <path d="M8 24h48" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                      <path d="M20 8h24" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                      <path d="M26 36h12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                      <circle cx="32" cy="36" r="0" fill="currentColor" />
                      <path d="M24 32h16M24 40h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
                    </svg>
                    <span className="text-sm font-medium">{emptyText}</span>
                  </div>
                </td>
              </tr>
            ) : (
              filteredData?.map((row, index) => {
                const id = row[keyToSelect] ?? index;
                const isSelected = selectedIds.includes(id);

                if (!rowRenderer) return null;

                return rowRenderer({
                  row,
                  index,
                  isSelected,
                  toggleSelect: () => toggleRow(id),
                  RowComponent,
                  CellComponent,
                  colSpan: skeletonCols,
                  // convenience: checkbox cell ready to drop in
                  CheckboxCell: isMultiSelect ? (
                    <CellComponent className="text-center w-10">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleRow(id)}
                        onClick={(e) => e.stopPropagation()}
                        className="h-4 w-4 rounded border-gray-300 text-green-500 focus:ring-green-500"
                      />
                    </CellComponent>
                  ) : null,
                });
              })
            )}
          </tbody>
        </table>
      </div>

      {pagination && !loading && (
        <Pagination
          className="border-t border-gray-200 dark:border-gray-700 px-4 py-3"
          currentPage={paginationState.currentPage}
          setCurrentPage={paginationState.setCurrentPage}
          rowsPerPage={paginationState.rowsPerPage}
          setRowsPerPage={paginationState.setRowsPerPage}
          totalPages={paginationState.totalPages}
          pageJump={paginationState.pageJump}
          setPageJump={paginationState.setPageJump}
          jumpToPage={paginationState.jumpToPage}
        />
      )}
    </div>
  );
};

export default Table2;
